import type {
  CustomElement,
  SharedSnippetReference,
  SharedSnippet,
  SharedSnippetProvider,
  SharedSnippetContent,
  SharedSnippetUpdateCallback,
  SharedSnippetMode,
} from '@aha-app/aha-editor-v2';
import { openSharedSnippetsModal } from './components/SharedSnippetsPicker';
import { rewriteKBURL } from '../KnowledgeBaseLinkRewriter/KnowledgeBaseLinkRewriterPlugin';
import AhaDrawer from 'javascripts/drawer';
import { jsonFetch } from 'javascripts/fetch';
import ReactiveRegister from 'javascripts/reactive_register';
import type AhaEditorFieldV2Controller from 'components/aha_editor_field_v2/controllers/AhaEditorFieldV2Controller';
import { pick } from 'lodash';
import AhaAnalytics from 'javascripts/analytics';

interface SharedSnippetPayload {
  shared_snippet?: SharedSnippet;
}

function rewriteURLs(
  editor_content: SharedSnippetContent | CustomElement
): void {
  if ('type' in editor_content && editor_content.type === 'link') {
    let href = rewriteKBURL(editor_content.link_href);

    if (href.startsWith('/')) {
      href = new URL(href, window.location.origin).toString();
    }

    editor_content.link_href = href;
  }

  if (editor_content.children) {
    for (const child of editor_content.children) {
      if (!('text' in child)) {
        rewriteURLs(child);
      }
    }
  }
}

function makeImmediateUpdateHandler(
  snippet: SharedSnippet,
  callback: SharedSnippetUpdateCallback
) {
  return function updateSnippetContent(e: CustomEvent) {
    if (!(e.target instanceof HTMLElement)) return;

    const container = e.target.closest(
      `[data-editable-field-id="${snippet.id}"]`
    );
    if (!container) return;

    callback(snippet.token, {
      ...snippet,
      editor_content: { ...snippet.editor_content, children: e.detail.value },
    });
  };
}

class AhaSharedSnippetProvider implements SharedSnippetProvider {
  type = 'sharedSnippet' as const;

  // Reactive updates key we registered for.
  reactiveKey: string;
  changeEventHandler: (e: CustomEvent) => void;

  constructor(private readonly controller: AhaEditorFieldV2Controller) {}

  featureEnabled(): boolean {
    return Boolean(
      window.currentAccount && window.currentAccount.hasSharedSnippets
    );
  }

  onDestroy() {
    if (this.reactiveKey) {
      ReactiveRegister.deregister(this.reactiveKey);
    }

    if (this.changeEventHandler) {
      document.removeEventListener(
        'aha-editor:change',
        this.changeEventHandler
      );
    }

    // TODO: we should really cancel any pending requests here too.
  }

  onGetSnippetContent(
    snippetToken: string,
    snippetUpdateCallback: SharedSnippetUpdateCallback
  ): void {
    if (window.knowledgeBaseSnippets) {
      if (snippetToken in window.knowledgeBaseSnippets) {
        const snippet = window.knowledgeBaseSnippets[snippetToken];

        if (snippet.shared_snippet?.editor_content) {
          rewriteURLs(snippet.shared_snippet.editor_content);
        }

        snippetUpdateCallback(snippetToken, snippet.shared_snippet);
      }

      return;
    }

    // Load a snippet and return it to the editor.
    const loadSnippet = async () => {
      const snippet = await this.loadSnippetContent(snippetToken);
      if (!snippet) return;
      snippetUpdateCallback(snippetToken, snippet);

      // Register to get reactive updates for the same snippet. This re-registers on
      // every load, but that should be safe.
      this.reactiveKey = `SharedSnippet-${snippet.id}`;
      ReactiveRegister.register(
        this.reactiveKey,
        `${this.reactiveKey}/Note`,
        () => {
          loadSnippet();
        }
      );
      this.changeEventHandler = makeImmediateUpdateHandler(
        snippet,
        snippetUpdateCallback
      );
      document.addEventListener('aha-editor:change', this.changeEventHandler);
    };
    loadSnippet();
  }

  async loadSnippetContent(snippetToken: string) {
    const response = await jsonFetch(
      `/shared_snippets/${snippetToken}.json`,
      {}
    );

    const snippetPayload = (await response.json()) as SharedSnippetPayload;

    return snippetPayload.shared_snippet;
  }

  createSharedSnippet = async (
    mode: SharedSnippetMode
  ): Promise<SharedSnippetReference | undefined> => {
    if (!this.controller.props.projectId) {
      return;
    }

    const createSharedSnippet = await jsonFetch('/shared_snippets', {
      method: 'POST',
      body: JSON.stringify({
        shared_snippet: {
          project_id: this.controller.props.projectId,
          mode,
        },
      }),
    });

    const sharedSnippet = (
      (await createSharedSnippet.json()) as SharedSnippetPayload
    ).shared_snippet;

    if (sharedSnippet) {
      AhaAnalytics.track(
        'Feature-7363748125797685395',
        'User creates snippet',
        {}
      );
      this.openSnippet(sharedSnippet.token);
      return pick(sharedSnippet, 'token', 'mode');
    }
  };

  onAddSharedSnippet = () => {
    return new Promise<SharedSnippetReference>(resolve => {
      openSharedSnippetsModal({
        callback: (snippetReference, options) => {
          if (snippetReference) {
            AhaAnalytics.track(
              'Feature-7363748125797685395',
              'User inserts snippet',
              {}
            );
          }

          resolve(snippetReference);

          if (options?.open && snippetReference) {
            this.openSnippet(snippetReference.token);
          }
        },
        onCreateSharedSnippet: this.controller.props.projectId
          ? this.createSharedSnippet
          : undefined,
        reactiveUrl: this.controller.props.projectId
          ? `/shared_snippets.modal?project_id=${this.controller.props.projectId}&all_in_account=true`
          : '/shared_snippets.modal',
      });
    });
  };

  onEditSharedSnippet(snippetToken: string) {
    if (!snippetToken) {
      return;
    }
    this.openSnippet(snippetToken);
  }

  openSnippet(snippetToken: string) {
    if (AhaDrawer.isOpen()) {
      window.open(
        `/shared_snippets/${snippetToken}`,
        '_blank',
        'noopener,noreferrer'
      );
    } else {
      AhaDrawer.showUrl(`/shared_snippets/${snippetToken}`);
    }
  }
}

export default AhaSharedSnippetProvider;
